import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Text, XStack, YStack } from "tamagui";
import { RiShieldCheckLine } from "react-icons/ri";
import { colors } from "@styles/colors";

/**
 * 模擬一卡通的授權扣款頁（⑨-1 授權條款 / ⑨-2 確認額度並送出）。
 *
 * ⚠️ 這頁**不是**一卡通的畫面，是本機驗證流程用的替身。真環境的 paymentUrl
 * 會喚醒 iPASS MONEY APP，由一卡通自己呈現條款與每月扣款上限。
 *
 * 為什麼需要它：mock 若直接跳回 confirmUrl，畫面會變成「選了付款方式就顯示
 * 已完成授權」——客戶沒同意過任何條款，在金流流程裡不能這樣演示，也驗不到
 * 「取消授權」那條分支。
 *
 * 每月扣款上限只是示意：規格的 A1 請求根本沒有這個欄位，它是客戶在 iPASS APP
 * 自己設的，我們既帶不進去也讀不回來（已列入待與一卡通確認事項）。
 */
export default function IPassMockAuthorize() {
  const [params] = useSearchParams();
  const transactionId = params.get("TransactionId") || "";
  const confirmUrl = params.get("confirm") || "/";
  const cancelUrl = params.get("cancel") || "/";
  const [agreed, setAgreed] = useState(false);

  const go = (base: string) => {
    const u = new URL(base, window.location.origin);
    u.searchParams.set("TransactionId", transactionId);
    window.location.href = u.toString();
  };

  return (
    <YStack f={1} background={colors.bg} p="$4" gap={14}>
      <YStack background="#fff4e6" p="$3" borderRadius={8} gap={4}>
        <Text fontSize={12} fontWeight="700" color="#f08c00">
          模擬畫面
        </Text>
        <Text fontSize={12} color={colors.sub}>
          這不是一卡通的頁面。真環境會開啟 iPASS MONEY APP，由一卡通呈現條款與額度設定。
        </Text>
      </YStack>

      <XStack ai="center" gap={8}>
        <RiShieldCheckLine size={22} color={colors.primary} />
        <Text fontSize={18} fontWeight="700" color={colors.text}>
          請確認您要授權扣款的相關內容
        </Text>
      </XStack>

      <YStack background="#fff" borderRadius={8} p="$3" gap={10}>
        <XStack jc="space-between">
          <Text color={colors.sub}>授權對象</Text>
          <Text color={colors.text} fontWeight="600">
            智器科技
          </Text>
        </XStack>
        <XStack jc="space-between">
          <Text color={colors.sub}>授權項目</Text>
          <Text color={colors.text} fontWeight="600">
            交易自動扣款
          </Text>
        </XStack>
        <XStack jc="space-between">
          <Text color={colors.sub}>每月扣款上限</Text>
          <Text color={colors.text} fontWeight="600">
            NT$ 5,000
          </Text>
        </XStack>
        <XStack jc="space-between">
          <Text color={colors.sub}>扣款來源</Text>
          <Text color={colors.text} fontWeight="600">
            帳戶餘額
          </Text>
        </XStack>
      </YStack>

      <YStack background="#fff" borderRadius={8} p="$3" gap={6}>
        <Text fontSize={13} color={colors.sub}>
          授權綁定電子支付帳戶（iPASS MONEY）自動扣款後，於您使用智器科技營運之平台
          所提供的服務時，瓦斯訂購配送將自動從 iPASS MONEY 電子支付帳戶中扣款支付。
        </Text>
        <Text fontSize={13} color={colors.sub}>
          申請綁定自動扣款成功後，自動扣款服務即刻生效，您可隨時申請解除綁定。
        </Text>
      </YStack>

      <Button
        chromeless
        jc="flex-start"
        onPress={() => setAgreed((v) => !v)}
        color={agreed ? colors.primary : colors.sub}
      >
        {agreed ? "☑" : "☐"}　我已閱讀並同意授權條款
      </Button>

      <Button
        backgroundColor={agreed ? colors.green : colors.gray200}
        color="#fff"
        disabled={!agreed}
        onPress={() => go(confirmUrl)}
      >
        同意並送出
      </Button>
      <Button chromeless color={colors.sub} onPress={() => go(cancelUrl)}>
        不同意
      </Button>
    </YStack>
  );
}
